import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { 
  User, 
  UserPlus, 
  Edit, 
  Trash2, 
  Shield, 
  HardDrive, 
  Save, 
  X, 
  Check, 
  Key, 
  AlertCircle
} from 'lucide-react'
import { 
  getAllUsers, 
  createUser, 
  updateUser, 
  deleteUser, 
  resetUserPassword 
} from '../services/authService'
import { useToast } from '../hooks/useToast'
import { formatFileSize } from '../utils/fileHelpers'

interface UserData {
  id: string
  username: string
  isAdmin?: boolean
  storageUsed: number
  storageLimit: number
}

const newUserSchema = z.object({
  username: z.string().min(3, 'Username must be at least 3 characters'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  isAdmin: z.boolean().default(false),
  storageLimit: z.coerce.number().min(1, 'Storage limit must be at least 1 GB')
})

const editUserSchema = z.object({
  username: z.string().min(3, 'Username must be at least 3 characters'),
  isAdmin: z.boolean(),
  storageLimit: z.coerce.number().min(1, 'Storage limit must be at least 1 GB')
})

const resetPasswordSchema = z.object({
  password: z.string().min(8, 'Password must be at least 8 characters')
})

type NewUserFormData = z.infer<typeof newUserSchema>
type EditUserFormData = z.infer<typeof editUserSchema>
type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>

const AdminPage: React.FC = () => {
  const [users, setUsers] = useState<UserData[]>([])
  const [loading, setLoading] = useState(true)
  const [isCreatingUser, setIsCreatingUser] = useState(false)
  const [editingUserId, setEditingUserId] = useState<string | null>(null)
  const [resetPasswordUserId, setResetPasswordUserId] = useState<string | null>(null)
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null)
  const { showToast } = useToast()
  
  const { 
    register: registerNewUser, 
    handleSubmit: handleNewUserSubmit, 
    formState: { errors: newUserErrors }, 
    reset: resetNewUserForm 
  } = useForm<NewUserFormData>({
    resolver: zodResolver(newUserSchema),
    defaultValues: {
      isAdmin: false,
      storageLimit: 5
    }
  })
  
  const { 
    register: registerEditUser, 
    handleSubmit: handleEditUserSubmit, 
    formState: { errors: editUserErrors }, 
    reset: resetEditUserForm 
  } = useForm<EditUserFormData>({
    resolver: zodResolver(editUserSchema)
  })
  
  const { 
    register: registerResetPassword, 
    handleSubmit: handleResetPasswordSubmit, 
    formState: { errors: resetPasswordErrors }, 
    reset: resetResetPasswordForm 
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema)
  })
  
  useEffect(() => {
    loadUsers()
  }, [])
  
  const loadUsers = async () => {
    try {
      setLoading(true)
      const usersData = await getAllUsers()
      setUsers(usersData)
    } catch (error) {
      console.error('Failed to load users:', error)
      showToast('Failed to load users', 'error')
    } finally {
      setLoading(false)
    }
  }
  
  const onCreateUser = async (data: NewUserFormData) => {
    try {
      await createUser(
        data.username, 
        data.password, 
        data.isAdmin, 
        data.storageLimit
      )
      showToast(`User "${data.username}" created successfully`, 'success')
      setIsCreatingUser(false)
      resetNewUserForm()
      loadUsers()
    } catch (error) {
      console.error('Failed to create user:', error)
      showToast(`Failed to create user: ${(error as Error).message}`, 'error')
    }
  }
  
  const startEditingUser = (user: UserData) => {
    setEditingUserId(user.id)
    resetEditUserForm({
      username: user.username,
      isAdmin: user.isAdmin || false,
      storageLimit: user.storageLimit / (1024 * 1024 * 1024) // Convert bytes to GB
    })
  }
  
  const onUpdateUser = async (data: EditUserFormData) => {
    if (!editingUserId) return
    
    try {
      await updateUser(editingUserId, {
        username: data.username,
        isAdmin: data.isAdmin,
        storageLimit: data.storageLimit
      })
      showToast(`User "${data.username}" updated successfully`, 'success')
      setEditingUserId(null)
      loadUsers()
    } catch (error) {
      console.error('Failed to update user:', error)
      showToast(`Failed to update user: ${(error as Error).message}`, 'error')
    }
  }
  
  const onResetPassword = async (data: ResetPasswordFormData) => {
    if (!resetPasswordUserId) return
    
    try {
      await resetUserPassword(resetPasswordUserId, data.password)
      showToast('Password reset successfully', 'success')
      setResetPasswordUserId(null)
      resetResetPasswordForm()
    } catch (error) {
      console.error('Failed to reset password:', error)
      showToast(`Failed to reset password: ${(error as Error).message}`, 'error')
    }
  }
  
  const confirmDeleteUser = (userId: string) => {
    setDeletingUserId(userId)
  }
  
  const onDeleteUser = async () => {
    if (!deletingUserId) return
    
    try {
      await deleteUser(deletingUserId)
      showToast('User deleted successfully', 'success')
      setDeletingUserId(null)
      loadUsers()
    } catch (error) {
      console.error('Failed to delete user:', error)
      showToast(`Failed to delete user: ${(error as Error).message}`, 'error')
    }
  }
  
  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">User Administration</h1>
        <button
          className="btn btn-primary flex items-center"
          onClick={() => setIsCreatingUser(true)}
        >
          <UserPlus size={18} className="mr-2" />
          New User
        </button>
      </div>
      
      {isCreatingUser && (
        <div className="bg-white dark:bg-dark-background rounded-lg shadow-custom-light p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Create New User</h2>
          <form onSubmit={handleNewUserSubmit(onCreateUser)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="new-username" className="block mb-1 font-medium text-[var(--color-primary)]">
                  Username
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <User size={18} className="text-[var(--color-accent)]" />
                  </div>
                  <input
                    id="new-username"
                    type="text"
                    className="input pl-10 w-full"
                    placeholder="Enter username"
                    {...registerNewUser('username')}
                  />
                </div>
                {newUserErrors.username && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{newUserErrors.username.message}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="new-password" className="block mb-1 font-medium text-[var(--color-primary)]">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <Key size={18} className="text-[var(--color-accent)]" />
                  </div>
                  <input
                    id="new-password"
                    type="password"
                    className="input pl-10 w-full"
                    placeholder="Enter password"
                    {...registerNewUser('password')}
                  />
                </div>
                {newUserErrors.password && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{newUserErrors.password.message}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="new-storage-limit" className="block mb-1 font-medium text-[var(--color-primary)]">
                  Storage Limit (GB)
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <HardDrive size={18} className="text-[var(--color-accent)]" />
                  </div>
                  <input
                    id="new-storage-limit"
                    type="number"
                    min="1"
                    step="1"
                    className="input pl-10 w-full"
                    placeholder="Enter storage limit in GB"
                    {...registerNewUser('storageLimit')}
                  />
                </div>
                {newUserErrors.storageLimit && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{newUserErrors.storageLimit.message}</p>
                )}
              </div>
              
              <div className="flex items-center">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="h-5 w-5 rounded border-[var(--color-accent)]"
                    {...registerNewUser('isAdmin')}
                  />
                  <span className="ml-2 text-[var(--color-primary)]">Admin privileges</span>
                </label>
              </div>
            </div>
            
            <div className="flex space-x-2">
              <button
                type="submit"
                className="btn btn-primary flex items-center"
              >
                <Check size={18} className="mr-2" />
                Create User
              </button>
              <button
                type="button"
                className="btn btn-outline flex items-center"
                onClick={() => {
                  setIsCreatingUser(false)
                  resetNewUserForm()
                }}
              >
                <X size={18} className="mr-2" />
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
      
      {loading ? (
        <div className="bg-white dark:bg-dark-background rounded-lg shadow-custom-light p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[var(--color-primary)] mx-auto mb-4"></div>
          <p>Loading users...</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-dark-background rounded-lg shadow-custom-light overflow-hidden">
          <div className="overflow-auto">
            <table className="min-w-full divide-y divide-[var(--color-accent)] dark:divide-[var(--color-secondary)] dark:divide-opacity-30">
              <thead className="bg-[var(--color-background)] dark:bg-[#1f1f1f] sticky top-0 z-10">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[var(--color-primary)] uppercase tracking-wider">
                    Username
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[var(--color-primary)] uppercase tracking-wider">
                    Role
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[var(--color-primary)] uppercase tracking-wider">
                    Storage Used
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[var(--color-primary)] uppercase tracking-wider">
                    Storage Limit
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[var(--color-primary)] uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-dark-background divide-y divide-[var(--color-accent)] dark:divide-[var(--color-secondary)] dark:divide-opacity-30">
                {users.map((user) => (
                  <React.Fragment key={user.id}>
                    <tr className="hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f]">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <User size={20} className="text-[var(--color-primary)] mr-2" />
                          <span className="font-medium">{user.username}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.isAdmin ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[var(--color-secondary)] text-white">
                            <Shield size={14} className="mr-1" />
                            Admin
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[var(--color-background)] dark:bg-[#1f1f1f] text-[var(--color-primary)]">
                            User
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {formatFileSize(user.storageUsed)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {formatFileSize(user.storageLimit)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex space-x-2">
                          <button
                            className="p-1 text-[var(--color-primary)] hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f] rounded"
                            onClick={() => startEditingUser(user)}
                            title="Edit user"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            className="p-1 text-[var(--color-primary)] hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f] rounded"
                            onClick={() => setResetPasswordUserId(user.id)}
                            title="Reset password"
                          >
                            <Key size={16} />
                          </button>
                          {user.username !== 'admin' && (
                            <button
                              className="p-1 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                              onClick={() => confirmDeleteUser(user.id)}
                              title="Delete user"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    
                    {/* Edit User Form */}
                    {editingUserId === user.id && (
                      <tr className="bg-[var(--color-background)] dark:bg-[#1f1f1f]">
                        <td colSpan={5} className="px-6 py-4">
                          <form onSubmit={handleEditUserSubmit(onUpdateUser)} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <label htmlFor="edit-username" className="block mb-1 font-medium text-[var(--color-primary)]">
                                  Username
                                </label>
                                <input
                                  id="edit-username"
                                  type="text"
                                  className="input w-full"
                                  {...registerEditUser('username')}
                                />
                                {editUserErrors.username && (
                                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{editUserErrors.username.message}</p>
                                )}
                              </div>
                              
                              <div>
                                <label htmlFor="edit-storage-limit" className="block mb-1 font-medium text-[var(--color-primary)]">
                                  Storage Limit (GB)
                                </label>
                                <input
                                  id="edit-storage-limit"
                                  type="number"
                                  min="1"
                                  step="1"
                                  className="input w-full"
                                  {...registerEditUser('storageLimit')}
                                />
                                {editUserErrors.storageLimit && (
                                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{editUserErrors.storageLimit.message}</p>
                                )}
                              </div>
                              
                              <div className="flex items-center">
                                <label className="flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    className="h-5 w-5 rounded border-[var(--color-accent)] dark:border-[var(--color-secondary)]"
                                    {...registerEditUser('isAdmin')}
                                  />
                                  <span className="ml-2 text-[var(--color-primary)]">Admin privileges</span>
                                </label>
                              </div>
                            </div>
                            
                            <div className="flex space-x-2">
                              <button
                                type="submit"
                                className="btn btn-primary flex items-center"
                              >
                                <Save size={18} className="mr-2" />
                                Save Changes
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline flex items-center"
                                onClick={() => setEditingUserId(null)}
                              >
                                <X size={18} className="mr-2" />
                                Cancel
                              </button>
                            </div>
                          </form>
                        </td>
                      </tr>
                    )}
                    
                    {/* Reset Password Form */}
                    {resetPasswordUserId === user.id && (
                      <tr className="bg-[var(--color-background)] dark:bg-[#1f1f1f]">
                        <td colSpan={5} className="px-6 py-4">
                          <form onSubmit={handleResetPasswordSubmit(onResetPassword)} className="space-y-4">
                            <div>
                              <label htmlFor="reset-password" className="block mb-1 font-medium text-[var(--color-primary)]">
                                New Password
                              </label>
                              <input
                                id="reset-password"
                                type="password"
                                className="input w-full"
                                placeholder="Enter new password"
                                {...registerResetPassword('password')}
                              />
                              {resetPasswordErrors.password && (
                                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{resetPasswordErrors.password.message}</p>
                              )}
                            </div>
                            
                            <div className="flex space-x-2">
                              <button
                                type="submit"
                                className="btn btn-primary flex items-center"
                              >
                                <Key size={18} className="mr-2" />
                                Reset Password
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline flex items-center"
                                onClick={() => setResetPasswordUserId(null)}
                              >
                                <X size={18} className="mr-2" />
                                Cancel
                              </button>
                            </div>
                          </form>
                        </td>
                      </tr>
                    )}
                    
                    {/* Delete User Confirmation */}
                    {deletingUserId === user.id && (
                      <tr className="bg-[var(--color-background)] dark:bg-[#1f1f1f]">
                        <td colSpan={5} className="px-6 py-4">
                          <div className="flex items-start">
                            <div className="flex-shrink-0 mt-1">
                              <AlertCircle size={24} className="text-red-500" />
                            </div>
                            <div className="ml-3 flex-1">
                              <h3 className="text-lg font-semibold text-red-600 dark:text-red-400">Delete User?</h3>
                              <p className="mt-1 text-[var(--color-accent)]">
                                This will permanently delete the user "{user.username}" and all their data. This action cannot be undone.
                              </p>
                              <div className="mt-3 flex space-x-2">
                                <button
                                  className="btn bg-red-500 text-white hover:bg-red-600 flex items-center"
                                  onClick={onDeleteUser}
                                >
                                  <Trash2 size={18} className="mr-2" />
                                  Delete User
                                </button>
                                <button
                                  className="btn btn-outline flex items-center"
                                  onClick={() => setDeletingUserId(null)}
                                >
                                  <X size={18} className="mr-2" />
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminPage